import { FILTER_SERIALIZERS } from '../../filters/filterSerializers';
import { validateFilterValue } from '../../filters/filterValidation';
import type { FilterState, FilterValue, ListFilterDef, TableColumn } from './TableTemplateTypes';

/**
 * Builds a bidirectional mapping between column keys and database names.
 * Returns: { keyToDbName: key→dbName, dbNameToKey: dbName→key }
 * Useful for filter parameter serialization and API response deserialization.
 */
export function buildColumnNameMappings<T>(columns: TableColumn<T>[]): {
  keyToDbName: Map<string, string>;
  dbNameToKey: Map<string, string>;
} {
  const keyToDbName = new Map<string, string>();
  const dbNameToKey = new Map<string, string>();

  for (const col of columns) {
    const key = String(col.key);
    const dbName = col.databaseName || key;
    keyToDbName.set(key, dbName);
    dbNameToKey.set(dbName, key);
  }

  return { keyToDbName, dbNameToKey };
}

/**
 * Resolves a frontend column key to its database column name.
 * Falls back to the original key when no mapping is found.
 * Usable by any API factory that needs to translate a sort key before sending it to the server.
 */
export function resolveDbSortBy<T>(sortBy: string, columns: TableColumn<T>[]): string {
  const { keyToDbName } = buildColumnNameMappings(columns);
  return keyToDbName.get(sortBy) ?? sortBy;
}

/**
 * Appends filter state as URL query parameters, translating each column key to its database name.
 * Format: <databaseName>=<serialized value>
 * Usable by any API factory that forwards a FilterState to the server.
 */
export function appendFilterParams<T>(
  qs: URLSearchParams,
  filters: FilterState<T>,
  columns: TableColumn<T>[],
): void {
  const { keyToDbName } = buildColumnNameMappings(columns);
  for (const [key, filterValue] of Object.entries(filters) as [string, FilterValue][]) {
    const dbName = keyToDbName.get(key) || key;
    const col = columns.find(c => String(c.key) === key);

    // Invalid values (out-of-range number, incoherent date range, …) are never
    // sent to the backend — same validator drives the inline error message.
    if (validateFilterValue(col?.filter, filterValue)) continue;

    // For list filters: a `serialize` hook (opt-in) fully owns emission when the
    // options don't map 1:1 to the column; otherwise expand any valueAliases
    // before the default serializer. e.g. { null: ['null', 'empty'] } → selecting
    // 'null' sends both sentinels.
    let effectiveValue = filterValue;
    if (filterValue.kind === 'list') {
      const listDef = col?.filter as ListFilterDef | undefined;
      if (listDef?.serialize) {
        listDef.serialize(qs, filterValue.values);
        continue;
      }
      const aliases = listDef?.valueAliases;
      if (aliases) {
        const expanded = filterValue.values.flatMap(v => aliases[v] ?? [v]);
        effectiveValue = { ...filterValue, values: expanded };
      }
    }

    FILTER_SERIALIZERS[effectiveValue.kind](qs, dbName, effectiveValue);
  }
}

/**
 * Remaps API response rows to frontend row shape using each column's databaseName.
 * Call this after fetching so the table only ever reads col.key — never databaseName.
 */
export function mapApiRows<T>(apiRows: Record<string, unknown>[], columns: TableColumn<T>[]): T[] {
  const { dbNameToKey } = buildColumnNameMappings(columns);

  return apiRows.map(apiRow => {
    const row: Record<string, unknown> = { ...apiRow };
    for (const [dbName, key] of dbNameToKey.entries()) {
      if (dbName in apiRow) {
        row[key] = apiRow[dbName];
        if (dbName !== key) {
          delete row[dbName]; // Remove database column name to avoid duplicates
        }
      }
    }
    return row as T;
  });
}
